import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { HlmIconComponent, HlmTooltipDirective } from '../../../../ui';
import type { FeatureTexts } from '../feature-texts.util';

/**
 * Help icon whose tooltip lists every text of a billing feature (EB-4502):
 * English, KV text, KV original (only when corrected), invoice text, code.
 *
 * Help-icon pattern (Style Guide §7.3): `lucideCircleHelp` inside a real
 * `<button>` carrying the accessible name and the tooltip. `hlmTooltip` is a
 * BrnTooltip (CDK overlay), so it is not clipped by the grid's scroll container,
 * and it opens on keyboard focus as well as on hover.
 */
@Component({
  selector: 'cmt-feature-help',
  standalone: true,
  imports: [HlmIconComponent, HlmTooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-template #details>
      <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-left">
        <dt class="text-muted-foreground">English</dt>
        <dd>{{ texts().english }}</dd>
        @if (texts().kv; as kv) {
          <dt class="text-muted-foreground">KV</dt>
          <dd>{{ kv }}</dd>
        }
        @if (texts().kvOriginal; as kvOriginal) {
          <dt class="text-muted-foreground">KV (original)</dt>
          <dd>{{ kvOriginal }}</dd>
        }
        @if (texts().invoice; as invoice) {
          <dt class="text-muted-foreground">On invoice</dt>
          <dd>{{ invoice }}</dd>
        }
        <dt class="text-muted-foreground">Code</dt>
        <dd class="font-mono-feat">{{ texts().code }}</dd>
      </dl>
    </ng-template>
    <!-- A real button: focusable, named and with a visible focus ring; the icon is decorative. -->
    <button
      type="button"
      class="relative inline-flex cursor-help items-center rounded-sm after:absolute after:-inset-3.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:[outline-color:var(--ring)]"
      data-testid="feature-help"
      [attr.aria-label]="'Feature texts: ' + texts().english"
      [hlmTooltip]="details">
      <hlm-icon name="lucideCircleHelp" size="1rem" aria-hidden="true" />
    </button>
  `,
})
export class FeatureHelpComponent {
  readonly texts = input.required<FeatureTexts>();
}
